import React from 'react';
import { X } from 'lucide-react';

export const JobIntelPanel: React.FC<{ active: boolean; children: React.ReactNode }> = ({ active, children }) =>
  active ? <div className="space-y-6 animate-fade-in" data-testid="job-intel-panel">{children}</div> : null;

export const ActionItemsPanel: React.FC<{ active: boolean; children: React.ReactNode }> = ({ active, children }) =>
  active ? <div className="space-y-6 animate-fade-in" onClick={event => event.stopPropagation()} data-testid="action-items-panel">{children}</div> : null;

export const ShiftsPanel: React.FC<{ active: boolean; children: React.ReactNode }> = ({ active, children }) =>
  active ? <div className="space-y-6 animate-fade-in" onClick={event => event.stopPropagation()} data-testid="shifts-panel">{children}</div> : null;

export const NotesPanel: React.FC<{ active: boolean; children: React.ReactNode }> = ({ active, children }) =>
  active ? <div className="space-y-6 animate-fade-in" onClick={event => event.stopPropagation()} data-testid="notes-panel">{children}</div> : null;

export const ShiftList: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const list = listRef.current;
    if (!list || !window.matchMedia) return;
    const mobile = window.matchMedia('(max-width: 767px)');
    let lastOpened: HTMLDetailsElement | null = null;
    const keepOneEditor = () => {
      if (!mobile.matches) return;
      const opened = Array.from(list.querySelectorAll<HTMLDetailsElement>(':scope > details[open]'));
      const keep = lastOpened && opened.includes(lastOpened) ? lastOpened : opened[0];
      // Close native disclosures without unmounting their forms or touching drafts.
      opened.forEach(editor => { if (editor !== keep) editor.open = false; });
    };
    const onToggle = (event: Event) => {
      const editor = event.target;
      if (!(editor instanceof HTMLDetailsElement) || editor.parentElement !== list || !editor.open) return;
      lastOpened = editor;
      keepOneEditor();
    };
    list.addEventListener('toggle', onToggle, true);
    mobile.addEventListener('change', keepOneEditor);
    keepOneEditor();
    return () => {
      list.removeEventListener('toggle', onToggle, true);
      mobile.removeEventListener('change', keepOneEditor);
    };
  }, []);
  return <div ref={listRef} className="space-y-4" data-testid="shift-list">{children}</div>;
};

export const ShiftEditor: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  <div className="p-4 sm:p-5 border-t border-white/10 space-y-6" data-testid="shift-editor">{children}</div>;

export const CrewRosterEditor: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  <div className="space-y-5" data-testid="crew-roster-editor">{children}</div>;

export const QuoteHistoryPanel: React.FC<{ children: React.ReactNode; open?: boolean; onDismiss?: () => void }> = ({ children, open = false, onDismiss }) => (
  <>
  {open && <button className="fixed inset-0 z-[210] bg-slate-950/70 backdrop-blur-sm 2xl:hidden" onClick={event => { event.stopPropagation(); onDismiss?.(); }} aria-label="Close quote history" />}
  <aside onClick={event => event.stopPropagation()} className={`fixed inset-y-0 right-0 z-[220] w-[min(94vw,30rem)] overflow-y-auto p-5 sm:p-7 space-y-4 custom-scrollbar bg-slate-900 border-l border-white/10 min-h-0 shrink-0 transition-transform duration-200 2xl:static 2xl:w-[28rem] 2xl:translate-x-0 2xl:visible 2xl:bg-slate-900/40 ${open ? 'visible translate-x-0' : 'invisible translate-x-full pointer-events-none 2xl:pointer-events-auto'}`} aria-label="Quote and blueprint history" data-testid="quote-history-panel">
    <div className="sticky top-0 z-10 bg-slate-900/95 pb-3 flex items-center justify-between">
      <h4 className="type-heading text-base text-slate-200">Quote &amp; Blueprint History</h4>
      <button onClick={event => { event.stopPropagation(); onDismiss?.(); }} className="2xl:hidden min-w-11 min-h-11 rounded-xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-brand-neonBlue" aria-label="Close quote history"><X size={22} /></button>
    </div>
    {children}
  </aside>
  </>
);
